// ---------------------------------------------------------------
// presenceService.js — Quién está en la farmacia cada día.
//
// POR QUÉ EXISTE ESTO
// El horario completo (`schedules`) lleva dentro si alguien está de
// BAJA, y una baja es dato de salud: categoría especial del RGPD.
// Antes el equipo entero se descargaba los horarios de todos para
// poder pintar "en la farmacia ahora", así que cada empleado tenía
// en su navegador las bajas de sus compañeros aunque la interfaz no
// se las enseñara.
//
// Ahora se guarda aparte un resumen por mes con lo justo:
// `presence/{YYYY-MM}` = { month, byUser: { uid: { "3": { t: [{start,
// end}] }, "10": { v: true } } } } — las horas de cada turno y si ese
// día está de vacaciones. Las VACACIONES sí salen: al equipo le hace
// falta saber con quién no contar. La BAJA no: es dato de salud, y un
// día de baja queda aquí igual que un día libre.
// ---------------------------------------------------------------
import { doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { sharedSubscribe } from "../utils/sharedSnapshot";

/**
 * Deja de un día lo que el equipo necesita saber:
 *  · las horas del turno, para saber quién atiende;
 *  · si está de VACACIONES, para saber con quién no contar.
 *
 * Lo que NO sale de aquí es la BAJA: es dato de salud y sólo la ven
 * el interesado y la dirección. Un día de baja se guarda igual que
 * un día libre —no se guarda nada—, así que desde fuera quien está
 * de baja y quien libra son indistinguibles.
 */
function soloTurnos(days) {
  const out = {};
  for (const [d, info] of Object.entries(days || {})) {
    if (info?.baja) continue; // la baja NO sale de aquí
    const turnos = (info?.shifts || [])
      .filter((s) => s?.start && s?.end)
      .map((s) => ({ start: s.start, end: s.end }));
    if (turnos.length) out[d] = { t: turnos };
    else if (info?.ownVacation) out[d] = { v: true };
  }
  return out;
}

/** Reescribe la presencia de un mes a partir de los horarios completos */
export function savePresence(key, daysByUid) {
  const byUser = {};
  for (const [uid, days] of Object.entries(daysByUid || {})) {
    byUser[uid] = soloTurnos(days);
  }
  return setDoc(doc(db, "presence", key), {
    month: key,
    byUser,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Presencia de VARIAS personas sin tocar la del resto del equipo.
 * `savePresence` reescribe el documento entero, así que sirve para el
 * mes completo pero borraría a los demás en un guardado parcial.
 */
export function savePresencePartial(key, daysByUid) {
  const byUser = {};
  for (const [uid, days] of Object.entries(daysByUid || {})) {
    byUser[uid] = soloTurnos(days);
  }
  return setDoc(
    doc(db, "presence", key),
    { month: key, byUser, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Actualiza la presencia de UNA persona sin tocar la del resto */
export function savePresenceForUser(key, uid, days) {
  return setDoc(
    doc(db, "presence", key),
    { month: key, byUser: { [uid]: soloTurnos(days) }, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Presencia del mes con la MISMA forma que `schedulesByUser`
 * ({ uid: { days: { "1": { shifts: [...] } } } }), para que los
 * componentes que ya la usaban no tengan que cambiar.
 */
export function subscribePresence(key, onData, onError) {
  return sharedSubscribe(
    `presence:${key}`,
    (cb, err) =>
      onSnapshot(
        doc(db, "presence", key),
        (snap) => {
          const byUser = snap.exists() ? snap.data()?.byUser || {} : {};
          const map = {};
          for (const [uid, dias] of Object.entries(byUser)) {
            const days = {};
            for (const [d, info] of Object.entries(dias || {})) {
              // Formato antiguo: el valor era el array de turnos a secas
              const turnos = Array.isArray(info) ? info : info?.t || [];
              days[d] = {
                shifts: turnos,
                hours: 0,
                baja: false,
                vacation: null,
                ownVacation: !Array.isArray(info) && !!info?.v,
              };
            }
            map[uid] = { userId: uid, month: key, days };
          }
          cb(map);
        },
        err
      ),
    onData,
    onError
  );
}
